// Always-visible fullscreen toggle. Calls requestFullscreen on tap
// and best-effort screen.orientation.lock('landscape') for Android
// Chrome (iOS Safari rejects — swallowed). Glyph swaps on
// `fullscreenchange` so the button reflects current state. Hides
// only on browsers without the Fullscreen API entirely.
//
// 2026-04-28 — desktop gating dropped per playtest; the button now
// shows on every platform that supports the API. Position negotiated
// with the daily panel (panel sits at top:16, right:16, width:260px)
// — the button anchors at top:14, right:286px so they don't collide.

import { isTouchDevice } from './touch';
import { haptic } from '../settings/prefs';

type FullscreenDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};

type FullscreenEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

type ScreenOrientationLock = ScreenOrientation & {
  lock?: (orientation: 'landscape' | 'portrait' | 'any') => Promise<void>;
};

function fsElement(): Element | null {
  const d = document as FullscreenDoc;
  return document.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

function fsSupported(): boolean {
  const root = document.documentElement as FullscreenEl;
  return typeof root.requestFullscreen === 'function'
      || typeof root.webkitRequestFullscreen === 'function';
}

async function enterFullscreen(): Promise<void> {
  const root = document.documentElement as FullscreenEl;
  try {
    if (typeof root.requestFullscreen === 'function') {
      await root.requestFullscreen();
    } else if (typeof root.webkitRequestFullscreen === 'function') {
      await root.webkitRequestFullscreen();
    }
  } catch {
    // user gesture mismatch / unsupported — silent
    return;
  }
  // Best-effort orientation lock. iOS Safari rejects with NotSupportedError;
  // swallow so the unhandled-rejection doesn't pollute the console.
  const so = screen.orientation as ScreenOrientationLock | undefined;
  if (so && typeof so.lock === 'function') {
    so.lock('landscape').catch(() => {});
  }
}

async function exitFullscreen(): Promise<void> {
  const d = document as FullscreenDoc;
  try {
    if (typeof document.exitFullscreen === 'function') {
      await document.exitFullscreen();
    } else if (typeof d.webkitExitFullscreen === 'function') {
      await d.webkitExitFullscreen();
    }
  } catch {
    // already out / not allowed
  }
}

export function mountFullscreenButton(parent: HTMLElement): { destroy: () => void } | null {
  if (!fsSupported()) return null;
  // Anchored to the top-right corner on every device. On desktop the
  // daily panel slides down to sit BELOW the button (see
  // src/ui/daily_panel.ts top: 62px) so they stack instead of
  // overlapping; on touch the safe-area inset keeps the button clear
  // of notches / rounded corners.
  void isTouchDevice;
  const btn = document.createElement('button');
  btn.setAttribute('aria-label', 'Toggle fullscreen');
  btn.style.cssText = `
    position: fixed;
    top: max(12px, env(safe-area-inset-top));
    right: max(12px, env(safe-area-inset-right));
    width: 38px; height: 38px;
    border: 1px solid rgba(255,255,255,.25);
    border-radius: 8px;
    background: rgba(8,10,14,.6);
    color: #e5e7eb;
    font: 700 18px system-ui, sans-serif;
    line-height: 1;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    z-index: 10002;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    touch-action: manipulation;
    user-select: none;
    box-shadow: 0 4px 12px rgba(0,0,0,.4);
  `;
  parent.appendChild(btn);

  // Idempotency guard — main.ts mounts this early (before the entrance
  // video) so it overlays everything; app.ts mounts it again later in
  // boot. The second mount is a no-op via the marker below.
  if ((window as unknown as { __smpFullscreenMounted?: true }).__smpFullscreenMounted) {
    btn.remove();
    return null;
  }
  (window as unknown as { __smpFullscreenMounted?: true }).__smpFullscreenMounted = true;

  const refresh = () => {
    btn.textContent = fsElement() ? '✖' : '⛶';
    btn.title = fsElement() ? 'Exit fullscreen' : 'Enter fullscreen';
  };
  refresh();

  const onClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    if (fsElement()) void exitFullscreen();
    else void enterFullscreen();
  };
  btn.addEventListener('pointerdown', onClick);

  document.addEventListener('fullscreenchange', refresh);
  document.addEventListener('webkitfullscreenchange', refresh);

  return {
    destroy: () => {
      btn.remove();
      document.removeEventListener('fullscreenchange', refresh);
      document.removeEventListener('webkitfullscreenchange', refresh);
    },
  };
}

// Mobile-only pause button — sits below the fullscreen button.
// Mobile players have no Esc key, so this is the only way they can
// reach the pause menu.
export function mountPauseButton(
  parent: HTMLElement,
  onPause: () => void,
): { destroy: () => void } | null {
  if (!isTouchDevice()) return null;

  const btn = document.createElement('button');
  btn.setAttribute('aria-label', 'Pause menu');
  btn.textContent = '☰';
  btn.style.cssText = `
    position: fixed;
    top: calc(max(12px, env(safe-area-inset-top)) + 46px);
    right: max(12px, env(safe-area-inset-right));
    width: 38px; height: 38px;
    border: 1px solid rgba(255,255,255,.25);
    border-radius: 8px;
    background: rgba(8,10,14,.6);
    color: #e5e7eb;
    font: 700 18px system-ui, sans-serif;
    line-height: 1;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    z-index: 9999;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    touch-action: manipulation;
    user-select: none;
    box-shadow: 0 4px 12px rgba(0,0,0,.4);
  `;
  parent.appendChild(btn);

  const onClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    haptic(15);
    onPause();
  };
  btn.addEventListener('pointerdown', onClick);

  return {
    destroy: () => { btn.remove(); },
  };
}
